import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './DataTable.module.scss';

import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';

import clsx from 'clsx';

import { useUserConfig } from 'hooks/useUserConfig';
import { useMobileViewport } from 'hooks/useMobileViewport';
import usePersistedColumnWidths from 'hooks/usePersistedColumnWidths';

import StyledTooltip from 'components/StyledTooltip';

import { TOOLTIP_ID } from 'constants/commonConstants';

export function DataTable({
  enableRowSelection,
  setSelectedRows,
  columns,
  data,
  isFilterApplied,
  isLoading,
  loadMore,
  tableKey,
  isMorePagesAvailable,
  tableRef,
  noDataText
}) {
  const isMobile = useMobileViewport();
  const { setColumnWidths } = usePersistedColumnWidths(tableKey);

  const { getTableLimit, isAllLimitEnabled } = useUserConfig();

  const [isFetching, setIsFetching] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const getRowId = useCallback((row) => row.id, []);

  useEffect(() => {
    if (!setSelectedRows) return;

    const handleSelectionState = (selections) => {
      setSelectedRows((prev) =>
        Object.keys(selections).map(
          (key) =>
            table.getSelectedRowModel().rowsById[key]?.original ||
            prev.find((row) => row._id === key)
        )
      );
    };

    handleSelectionState(rowSelection);
  }, [rowSelection]);

  const table = useReactTable({
    data,
    defaultColumn: { minSize: 52 },
    enableColumnResizing: !isMobile,
    columns,
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId,
    enableRowSelection:
      typeof enableRowSelection === 'function'
        ? (row) => enableRowSelection(row)
        : enableRowSelection ?? true,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes = {};
    for (let i = 0; i < headers.length; i = i + 1) {
      const header = headers[i];
      const size = header.getSize();
      colSizes[`--header-${header.id}-size`] = size;
      colSizes[`--col-${header.column.id}-size`] = size;
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, columns]);

  const loadMoreData = () => {
    return new Promise((resolve) => {
      loadMore();
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  };

  const handleScroll = useCallback(() => {
    if (!tableKey) return;

    const limit = getTableLimit(tableKey);
    const allLimitEnabled = isAllLimitEnabled(tableKey);

    if (limit === 100 && allLimitEnabled && isMorePagesAvailable) {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 200 && !isFetching) {
        setIsFetching(true);
        loadMoreData()
          .then(() => {
            setIsFetching(false);
          })
          .catch(() => {
            setIsFetching(false);
          });
      }
    }
  }, [tableKey, getTableLimit, isAllLimitEnabled, isMorePagesAvailable, isFetching, loadMoreData]);

  useEffect(() => {
    if (!tableRef) return;

    const currentScrollRef = tableRef.current;
    currentScrollRef.addEventListener('scroll', handleScroll);

    return () => {
      currentScrollRef.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, tableRef]);

  useEffect(() => {
    return () => {
      if (!setColumnWidths) return;
      const currentSizing = table.getState().columnSizing;
      setColumnWidths(currentSizing);
    };
  }, [table, setColumnWidths]);

  return (
    <div ref={tableRef} className={styles.container}>
      <StyledTooltip />

      <div className={styles.table} style={{ ...columnSizeVars, width: table.getTotalSize() }}>
        <Header table={table} />

        {table.getState().columnSizingInfo.isResizingColumn ? (
          <MemoizedTableBody
            table={table}
            isFilterApplied={isFilterApplied}
            isLoading={isLoading}
          />
        ) : (
          <TableBody
            table={table}
            isFilterApplied={isFilterApplied}
            isLoading={isLoading}
            noDataText={noDataText}
          />
        )}
      </div>
    </div>
  );
}

const Header = ({ table }) => (
  <div className={styles.head}>
    {table.getHeaderGroups().map((headerGroup) => {
      return (
        <div key={headerGroup.id} className={styles.row}>
          {headerGroup.headers.map((header) => (
            <div
              key={header.id}
              className={clsx(
                styles.th,
                header.column.columnDef?.centerContent && styles.centered,
                header.column.columnDef?.sticky && styles.sticky
              )}
              style={{
                width: `calc(var(--header-${header?.id}-size) * 1px)`
              }}>
              {flexRender(header.column.columnDef.header, header.getContext())}
              <div
                className={clsx(styles.resizer, header.column.getIsResizing() && styles.isResizing)}
                style={{
                  transform: header.column.getIsResizing()
                    ? `translateX(${1 * (table.getState().columnSizingInfo.deltaOffset ?? 0)}px)`
                    : ''
                }}
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
              />
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

function TableBody({ table, isFilterApplied, isLoading, noDataText }) {
  const [activeRowId, setActiveRowId] = useState(null);

  const isCellEditable = (cell) => {
    if (!cell.column.columnDef?.editable) return false;

    const { editable } = cell.column.columnDef;

    if (typeof editable === 'boolean') {
      return !!editable;
    }

    const { original } = cell.row;

    return editable(original);
  };

  const setActiveRow = (rowId) => rowId !== activeRowId && setActiveRowId(rowId);
  const resetActiveRow = () => setActiveRowId(null);

  return (
    <div className={styles.body}>
      {table.getRowModel().rows?.length ? (
        table.getRowModel().rows.map((row) => (
          <div key={row.id} className={clsx(styles.row, activeRowId === row.id && styles.active)}>
            {row.getVisibleCells().map((cell) => (
              <div
                key={cell.id}
                className={clsx(
                  styles.td,
                  cell.column.columnDef?.centerContent && styles.centered,
                  cell.column.columnDef?.sticky && styles.sticky,
                  isCellEditable(cell) && styles.editable
                )}
                style={{
                  width: `calc(var(--col-${cell.column.id}-size) * 1px)`
                }}
                onClick={() => {
                  if (isCellEditable(cell) && cell.column.columnDef?.onEditClick) {
                    cell.column.columnDef.onEditClick(
                      cell.row.original,
                      cell.row.index,
                      resetActiveRow
                    );
                    setActiveRow(row.id);
                  }
                }}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={
                  cell.column.columnDef?.tooltipValueGetter
                    ? cell.column.columnDef?.tooltipValueGetter(cell.row.original)
                    : ''
                }>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ))}
          </div>
        ))
      ) : isLoading ? null : (
        <div className={styles.emptyRow}>
          <div className={styles.emptyTd}>
            {isFilterApplied
              ? 'No matches in the table'
              : noDataText || 'No available data in the table.'}
          </div>
        </div>
      )}
    </div>
  );
}

export const MemoizedTableBody = React.memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data
);
